import { mainSiteUrl, categories } from '@/mocks/categories';
import Navbar from '@/pages/home/components/Navbar';
import HeroSection from '@/pages/home/components/HeroSection';
import CategoryGrid from '@/pages/home/components/CategoryGrid';
import Footer from '@/pages/home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssspreadsheet.bond';

export default function Home() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "CSSBuy Spreadsheet — Ultimate Verified Product Database",
            "description": "Browse thousands of verified product listings from Taobao, Weidian, and 1688. Your trusted CSSBuy shopping companion.",
            "url": `${siteUrl}/`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "CSSBuy Spreadsheet",
              "url": siteUrl
            },
            "about": {
              "@type": "Thing",
              "name": "CSSBuy Spreadsheet"
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": `${siteUrl}/` }
              ]
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "CSSBuy Spreadsheet Categories",
            "description": "Browse verified CSSBuy Spreadsheet products by category — shoes, hoodies, jackets, t-shirts, accessories, bags, and more.",
            "url": `${siteUrl}/`,
            "numberOfItems": categories.length,
            "itemListElement": categories.map((cat, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "name": cat.name,
              "url": cat.url
            }))
          })
        }}
      />

      <Navbar />
      <HeroSection />

      {/* Feature Grid */}
      <section className="py-14 md:py-18 px-6 md:px-10 lg:px-16 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {/* Article Card */}
            <a
              href="/article/cssbuy-spreadsheet-guide"
              className="group relative rounded-xl overflow-hidden bg-foreground-900 p-5 md:p-6 flex flex-col justify-between min-h-[220px] md:min-h-[260px] cursor-pointer hover:bg-foreground-800 transition-colors"
            >
              <div>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-foreground-700/40 text-white/80 text-xs font-medium rounded-full mb-4 whitespace-nowrap">
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-article-line"></i></span>
                  Featured Guide
                </span>
                <h3 className="font-heading text-xl md:text-2xl text-white font-semibold leading-snug mb-2">
                  Ultimate CSSBuy Spreadsheet Guide
                </h3>
                <p className="text-white/55 text-sm leading-relaxed line-clamp-3">
                  Master the art of finding verified products on Taobao, Weidian, and 1688. Learn how to QC, compare prices, and shop with confidence.
                </p>
              </div>
              <span className="inline-flex items-center gap-1 text-accent-400 text-sm font-medium group-hover:gap-2 transition-all mt-4 whitespace-nowrap">
                Read the Guide
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
              </span>
            </a>

            {/* Visual Card 1 */}
            <div className="relative rounded-xl overflow-hidden min-h-[220px] md:min-h-[260px]">
              <img
                src="https://readdy.ai/api/search-image?query=Curated%20streetwear%20fashion%20collection%20flatlay%20with%20designer%20sneakers%20oversized%20hoodie%20and%20accessories%20on%20minimal%20cream%20backdrop%2C%20editorial%20product%20photography%2C%20soft%20diffused%20lighting%2C%20warm%20earthy%20tones%2C%20clean%20composition%2C%20high-end%20catalog%20aesthetic%2C%20textured%20fabrics%20visible%2C%20professional%20styling&width=800&height=600&seq=feature-streetwear-01&orientation=landscape"
                alt="Curated streetwear collection"
                title="CSSBuy Spreadsheet — Curated Streetwear Drops"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute top-4 left-4">
                <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full whitespace-nowrap">
                  <span className="w-3 h-3 inline-flex items-center justify-center mr-1"><i className="ri-fire-line"></i></span>
                  Streetwear
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white text-lg md:text-xl font-heading font-semibold">Curated Drops</p>
              </div>
            </div>

            {/* Visual Card 2 */}
            <div className="relative rounded-xl overflow-hidden bg-white border border-background-200/70 p-5 md:p-6 flex flex-col min-h-[220px] md:min-h-[260px]">
              <h3 className="font-heading text-xl md:text-2xl text-foreground-900 font-semibold mb-2">Top Brands</h3>
              <p className="text-foreground-500 text-sm mb-4">Nike, Adidas, Jordan, Supreme, Stussy, Stone Island &amp; more</p>
              <div className="flex-1 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Collection%20of%20popular%20streetwear%20brand%20logos%20and%20product%20details%20Nike%20Adidas%20Supreme%20Stussy%20arranged%20on%20neutral%20background%2C%20editorial%20fashion%20photography%2C%20soft%20natural%20light%2C%20minimal%20composition%2C%20warm%20cream%20backdrop%2C%20professional%20product%20styling%2C%20clean%20aesthetic&width=600&height=400&seq=feature-brands-02&orientation=landscape"
                  alt="Popular streetwear brands"
                  title="CSSBuy Spreadsheet — Top Fashion Brands"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {['Nike', 'Adidas', 'Jordan', 'Supreme', 'Stussy'].map((brand) => (
                  <a
                    key={brand}
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs font-medium rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {brand}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CategoryGrid />

      {/* SEO Content Section */}
      <section className="py-16 md:py-20 px-6 md:px-10 lg:px-16 bg-background-50">
        <article className="max-w-4xl mx-auto">
          <div className="mb-10">
            <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-full mb-3 whitespace-nowrap">
              CSSBuy Spreadsheet
            </span>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-900 font-semibold leading-tight mb-4">
              What Is a <span className="italic">CSSBuy Spreadsheet</span> and Why You Need One
            </h2>
            <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400 mb-6">
              <span>Updated: July 2026</span>
              <span>·</span>
              <span>10 min read</span>
              <span>·</span>
              <span>CSSBuy Guides</span>
            </div>

            <div className="bg-background-100 border-l-4 border-primary-400 rounded-r-lg p-4 md:p-5 mb-8">
              <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
                <strong className="text-foreground-900">CSSBuy Spreadsheet</strong> is a meticulously curated product database that helps international shoppers discover and purchase verified items from Chinese marketplaces — including Taobao, Weidian, and 1688 — through the CSSBuy shopping agent. It transforms hours of manual searching into a streamlined browsing experience by organizing thousands of trusted listings across shoes, hoodies, jackets, accessories, and more into clean, searchable categories.
              </p>
            </div>
          </div>

          <div className="prose-custom space-y-6 text-foreground-700 leading-relaxed">
            <h3 className="font-heading text-xl text-foreground-900 font-semibold mt-8 mb-3">The Problem with Manual Searching</h3>
            <p className="text-sm md:text-base">
              Anyone who has tried browsing Taobao or Weidian without guidance knows the struggle. The search algorithms prioritize paid listings over quality, descriptions are often inaccurate, and seller ratings can be misleading. Without a reliable <strong>CSSBuy Spreadsheet</strong>, shoppers waste countless hours scrolling through irrelevant results, comparing prices across dozens of tabs, and hoping the product that arrives matches the listing photos.
            </p>
            <p className="text-sm md:text-base">
              The reality is that Chinese marketplaces contain millions of listings — many of which are duplicates, low-quality knockoffs, or outright scams. A verified <strong>CSSBuy Spreadsheet</strong> acts as your personal filter, surfacing only the products that have been purchased, QC-checked, and reviewed by real community members. This collaborative verification process is what separates a spreadsheet-based approach from blindly trusting marketplace search results.
            </p>

            <h3 className="font-heading text-xl text-foreground-900 font-semibold mt-8 mb-3">How the CSSBuy Spreadsheet Works</h3>
            <p className="text-sm md:text-base">
              At its core, the <strong>CSSBuy Spreadsheet</strong> is a living document maintained by experienced shoppers who contribute verified product links along with key details: actual QC photos, weight estimates, sizing notes, price comparisons across sellers, and honest reviews about quality and accuracy. Each entry typically includes the product name, marketplace link, agent-specific notes, and community ratings — making it possible to shop with confidence even if you are brand new to the CSSBuy ecosystem.
            </p>
            <p className="text-sm md:text-base">
              Categories are organized intuitively: Shoes (sneakers, boots, slides), Hoodies &amp; Sweaters, T-Shirts, Jackets (bombers, puffer coats, denim jackets), Pants &amp; Shorts, Headwear (caps, beanies), Sets (matching tracksuits and coordinated outfits), Underwear &amp; Underpants, Jersey (sports jerseys from NBA, NFL, football clubs), Accessories (watches, jewelry, sunglasses, phone cases), Bags (backpacks, crossbody bags, totes), Belts, Perfumes, and a catch-all Other Stuff category for everything else.
            </p>

            <h3 className="font-heading text-xl text-foreground-900 font-semibold mt-8 mb-3">Why Community Verification Matters</h3>
            <p className="text-sm md:text-base">
              The true strength of any <strong>CSSBuy Spreadsheet</strong> lies in its community-driven verification model. Unlike automated price comparison tools that can be manipulated by sellers gaming the system, a spreadsheet curated by actual buyers provides authentic firsthand information. When a community member adds a product, they typically include QC photos showing the actual item received — not the polished marketing images from the listing. This transparency allows shoppers to assess quality, spot flaws, and make informed decisions before adding anything to their cart.
            </p>

            <h3 className="font-heading text-xl text-foreground-900 font-semibold mt-8 mb-3">Getting Started with Your First Purchase</h3>
            <p className="text-sm md:text-base">
              If you are new to using a <strong>CSSBuy Spreadsheet</strong>, the process is straightforward. Start by browsing the category that interests you — say, Nike sneakers. The spreadsheet will show you verified links organized by model, batch, and seller reputation. Once you find a pair you want, copy the product link into the CSSBuy agent platform, specify your size and any special instructions, and complete the payment. The agent handles purchasing from the seller, receiving the item at their warehouse, taking QC photos for your review, and shipping the package internationally once you approve.
            </p>
            <p className="text-sm md:text-base">
              One of the most valuable aspects of using a spreadsheet approach is the pricing transparency. You can instantly compare what different sellers charge for the same batch — something nearly impossible to do by manually searching each marketplace. Savvy shoppers often save 20-40% simply by referencing the spreadsheet to find the best-priced seller for the exact same product batch.
            </p>

            <h3 className="font-heading text-xl text-foreground-900 font-semibold mt-8 mb-3">Staying Updated with the Latest Finds</h3>
            <p className="text-sm md:text-base">
              The <strong>CSSBuy Spreadsheet</strong> is continuously updated as new products drop, old listings go out of stock, and community members discover fresh sellers. This dynamic nature means the spreadsheet you browse today may look different next week — which is exactly why bookmarking and checking back regularly is essential for serious shoppers. Subscribing to update notifications ensures you never miss a restock of sold-out items or the addition of highly anticipated new releases.
            </p>
          </div>

          <div className="mt-10 pt-8 border-t border-background-200/60">
            <a
              href="/article/cssbuy-spreadsheet-guide"
              className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-white text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Read the Full Guide
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
            </a>
          </div>
        </article>
      </section>

      {/* Trust Section */}
      <section className="py-16 md:py-20 px-6 md:px-10 lg:px-16 bg-background-100">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row gap-8 md:gap-12 items-center">
          <div className="flex-1">
            <span className="inline-flex items-center gap-1.5 text-foreground-500 text-xs font-medium mb-4 whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-foreground-800 inline-block"></span>
              Community Trusted
            </span>
            <blockquote className="font-heading text-xl md:text-2xl lg:text-3xl text-foreground-900 font-medium italic leading-relaxed mb-4">
              &ldquo;The CSSBuy Spreadsheet saved me countless hours. I found verified Nike Dunks and Stone Island pieces that all passed QC with flying colors. This is hands down the best resource for anyone using CSSBuy.&rdquo;
            </blockquote>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">Marcus R.</span>
              <span className="w-4 h-4 flex items-center justify-center text-accent-500"><i className="ri-verified-badge-fill"></i></span>
              <span className="text-xs text-foreground-400 whitespace-nowrap">Verified CSSBuy User</span>
            </div>
          </div>
          <div className="w-full md:w-[40%] aspect-[3/4] rounded-xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20a%20stylish%20young%20man%20in%20casual%20streetwear%20against%20clean%20white%20studio%20background%2C%20editorial%20fashion%20photography%2C%20soft%20natural%20lighting%2C%20warm%20neutral%20tones%2C%20minimal%20composition%2C%20looking%20confident%2C%20modern%20aesthetic&width=600&height=800&seq=trust-testimonial-01&orientation=portrait"
              alt="Satisfied CSSBuy Spreadsheet user testimonial"
              title="CSSBuy Spreadsheet Community Review"
              className="w-full h-full object-cover object-center"
            />
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-22 px-6 md:px-10 lg:px-16 bg-background-50 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-900 font-semibold mb-3">
            Ready to Start Shopping?
          </h2>
          <p className="text-foreground-500 text-sm md:text-base mb-8 max-w-md mx-auto">
            Browse our verified CSSBuy Spreadsheet and discover thousands of QC-checked products across all categories on our main store.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              cssbuy sheet
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-accent-500 text-white text-sm font-medium rounded-full hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              buy it now
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}